import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  useTheme,
  alpha,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Alert
} from '@mui/material';
import {
  Assignment as AssignmentIcon,
  Gavel as GavelIcon,
  Business as BusinessIcon,
  Payment as PaymentIcon,
  Cancel as CancelIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { useSEO } from '../hooks/useSEO';

const Terms = () => {
  const theme = useTheme();
  useSEO('服务条款');

  const termsSections = [
    {
      title: '服务内容',
      icon: <BusinessIcon />,
      content: [
        '希航教育提供的服务包括但不限于：',
        '• 马来西亚院校申请咨询和指导',
        '• 专业选择和课程规划建议',
        '• 申请材料准备和审核',
        '• 签证申请指导和协助',
        '• 语言培训推荐和安排',
        '• 住宿和接机服务安排',
        '• 留学期间后续支持服务'
      ]
    },
    {
      title: '服务费用',
      icon: <PaymentIcon />,
      content: [
        '关于服务费用的说明：',
        '• 咨询服务费用根据服务内容和服务周期确定',
        '• 院校申请费用包含申请费、材料翻译费等',
        '• 签证服务费用包含签证费、材料准备费等',
        '• 所有费用将在服务开始前明确告知并签署协议',
        '• 费用支付方式包括银行转账、支付宝、微信支付等',
        '• 发票将在费用支付后7个工作日内提供'
      ]
    },
    {
      title: '客户义务',
      icon: <CheckCircleIcon />,
      content: [
        '客户需要履行的义务：',
        '• 提供真实、准确、完整的个人信息和申请材料',
        '• 按照约定时间支付相关费用',
        '• 积极配合我们的服务流程和要求',
        '• 及时提供所需的补充材料和信息',
        '• 遵守相关法律法规和院校规定',
        '• 不得提供虚假信息或隐瞒重要事实',
        '• 保护自己的账户信息和隐私安全'
      ]
    },
    {
      title: '服务限制',
      icon: <WarningIcon />,
      content: [
        '服务范围和限制：',
        '• 我们仅提供咨询和指导服务，不保证申请结果',
        '• 最终申请结果由院校和相关部门决定',
        '• 因客户提供虚假信息导致的后果由客户承担',
        '• 因不可抗力因素导致的服务中断不承担责任',
        '• 服务范围仅限于马来西亚留学相关业务',
        '• 超出服务范围的要求需要另行协商',
        '• 客户应自行承担相关风险和后果'
      ]
    },
    {
      title: '退款政策',
      icon: <CancelIcon />,
      content: [
        '关于退款的说明：',
        '• 在服务开始前，客户可以申请全额退款',
        '• 服务开始后，根据已完成的服务内容按比例退款',
        '• 因客户原因导致的退款，扣除已产生的成本费用',
        '• 因我方原因导致的退款，全额退还已支付费用',
        '• 申请费、签证费等第三方费用不予退还',
        '• 退款申请需要在服务终止后30天内提出',
        '• 退款将在申请审核通过后7个工作日内处理'
      ]
    },
    {
      title: '责任限制',
      icon: <GavelIcon />,
      content: [
        '责任限制和免责声明：',
        '• 我们不对申请结果承担保证责任',
        '• 因第三方原因导致的问题不承担责任',
        '• 因客户违反协议导致的损失不承担责任',
        '• 因不可抗力因素导致的损失不承担责任',
        '• 我们的责任总额不超过已收取的服务费用',
        '• 客户应自行承担申请过程中的相关风险',
        '• 建议客户购买相关保险以降低风险'
      ]
    }
  ];

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
      {/* 英雄区域 */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`,
          py: 8,
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
              <AssignmentIcon sx={{ fontSize: 64, color: theme.palette.primary.main }} />
            </Box>
            <Typography
              variant="h2"
              sx={{
                mb: 3,
                fontWeight: 700,
                background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}
            >
              服务条款
            </Typography>
            <Typography variant="h5" sx={{ color: 'text.secondary', lineHeight: 1.8 }}>
              请仔细阅读以下服务条款，使用我们的服务即表示您同意遵守这些条款
            </Typography>
            <Box sx={{ mt: 3 }}>
              <Chip 
                label="最后更新：2025年1月" 
                color="primary" 
                variant="outlined"
                sx={{ fontWeight: 600 }}
              />
            </Box>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 8 }}>
        {/* 重要提示 */}
        <Alert 
          severity="info" 
          sx={{ 
            mb: 6, 
            borderRadius: 3,
            '& .MuiAlert-icon': {
              fontSize: 28
            }
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
            重要提示
          </Typography>
          <Typography variant="body1">
            本服务条款是您与希航教育之间具有法律约束力的协议。在开始使用我们的服务之前，
            请仔细阅读并理解所有条款。如果您不同意这些条款，请不要使用我们的服务。
          </Typography>
        </Alert>

        {/* 引言 */}
        <Paper
          sx={{
            p: 4,
            mb: 6,
            borderRadius: 3,
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
          }}
        >
          <Typography variant="h5" sx={{ mb: 3, fontWeight: 600, color: 'text.primary' }}>
            引言
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.8, color: 'text.secondary' }}>
            欢迎使用希航教育的留学服务。本服务条款（以下简称"条款"）规定了您使用我们服务时
            的权利和义务。通过使用我们的服务，您表示已阅读、理解并同意受本条款的约束。
            如果您代表公司或其他法律实体使用我们的服务，您声明您有权代表该实体同意本条款。
          </Typography>
        </Paper>

        {/* 服务条款内容 */}
        <Box sx={{ mb: 6 }}>
          {termsSections.map((section, index) => (
            <Paper
              key={index}
              sx={{
                p: 4,
                mb: 4,
                borderRadius: 3,
                transition: 'all 0.3s ease',
                border: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`,
                '&:hover': {
                  boxShadow: theme.shadows[4],
                  transform: 'translateY(-2px)'
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                    color: theme.palette.primary.main,
                    mr: 2
                  }}
                >
                  {section.icon}
                </Box>
                <Typography variant="h5" sx={{ fontWeight: 600, color: 'text.primary' }}>
                  {section.title}
                </Typography>
              </Box>
              <List sx={{ pl: 0 }}>
                {section.content.map((item, itemIndex) => (
                  <ListItem key={itemIndex} sx={{ pl: 0, py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 24, mr: 1 }}>
                      {item.startsWith('•') ? (
                        <Box
                          sx={{
                            width: 6,
                            height: 6,
                            borderRadius: '50%',
                            backgroundColor: theme.palette.primary.main
                          }}
                        />
                      ) : (
                        <Box sx={{ width: 6 }} />
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography
                          variant="body1"
                          sx={{
                            lineHeight: 1.6,
                            color: item.startsWith('•') ? 'text.secondary' : 'text.primary',
                            fontWeight: item.startsWith('•') ? 400 : 500
                          }}
                        >
                          {item}
                        </Typography>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </Paper>
          ))}
        </Box>

        {/* 争议解决 */}
        <Paper
          sx={{
            p: 4,
            mb: 6,
            borderRadius: 3,
            background: `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.05)} 0%, ${alpha(theme.palette.error.main, 0.05)} 100%)`,
            border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`
          }}
        >
          <Typography variant="h5" sx={{ mb: 3, fontWeight: 600, color: 'text.primary' }}>
            争议解决
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, color: 'text.secondary', lineHeight: 1.8 }}>
            如因本条款或服务产生任何争议，双方应首先通过友好协商解决。协商不成的，
            任何一方均可向有管辖权的人民法院提起诉讼。
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.8 }}>
            本条款的适用法律为中华人民共和国法律。
          </Typography>
        </Paper>

        {/* 联系我们 */}
        <Paper
          sx={{
            p: 4,
            borderRadius: 3,
            background: `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.05)} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
            border: `1px solid ${alpha(theme.palette.secondary.main, 0.2)}`,
            textAlign: 'center'
          }}
        >
          <Typography variant="h5" sx={{ mb: 3, fontWeight: 600, color: 'text.primary' }}>
            联系我们
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary', lineHeight: 1.8 }}>
            如果您对本服务条款有任何疑问或需要澄清，请通过以下方式与我们联系：
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              邮箱：xihangedu@qq.com
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              电话：0551-62888215
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              地址：合肥市包河区万达广场7号写字楼1805室
            </Typography>
          </Box>
        </Paper>

        <Divider sx={{ my: 6 }} />

        {/* 条款更新 */}
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="body2" sx={{ color: 'text.disabled' }}>
            我们保留随时修改本服务条款的权利。修改后的条款将在本页面上发布，
            并在适当情况下通过其他方式通知您。您继续使用我们的服务即表示您接受修改后的条款。
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default Terms;
